/**
 * AI CONTEXT: mobile-first - User registration page with age verification
 * 
 * This component implements user registration with:
 * - Mobile-first responsive design
 * - Age verification for legal compliance
 * - Form validation and error handling
 * - AWS Cognito integration
 * - Touch-friendly interface elements
 * - Required shipping address collection
 * - SMS consent for Twilio notifications
 * 
 * Architecture decisions:
 * - Use controlled components for form state
 * - Implement real-time validation
 * - Provide clear error messages
 * - Follow mobile-first design principles
 * - Collect shipping address during registration for immediate profile setup
 * - Collect SMS consent for marketing and notification purposes
 * 
 * Future considerations:
 * - Add social login options
 * - Implement biometric registration
 * - Add profile picture upload
 * - Consider multi-step registration flow
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Calendar, Phone, Mail, User, Lock, MapPin, MessageSquare, Tag } from 'lucide-react';
import { t } from '../i18n';
import { updateMyProfile, sendWelcomeSMS, isApiConfigured, recordReferral } from '../services/apiClient';
import { setOrCreateMeta } from '../utils/seo';
import { ensureDate } from '../utils/date';
import toast from 'react-hot-toast';

const Register = () => {
  const navigate = useNavigate();
  const { register, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    birthDate: '',
    // Referral code (optional)
    referralCode: '',
    // Shipping address fields
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
    // SMS consent
    smsConsent: false,
    // Push notification consent
    pushConsent: false
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // AI CONTEXT: referral-system - Check for pending referral code from shareable link
  // Reasoning: When users arrive via /share/{code}, pre-populate the referral field
  // Constraints: Do NOT clear pendingReferralCode here—only clear after successful record-referral
  // so a refresh or back-navigation doesn't lose the code (see handleSubmit)
  useEffect(() => {
    const pendingCode = localStorage.getItem('pendingReferralCode');
    if (pendingCode) {
      setFormData(prev => ({ ...prev, referralCode: pendingCode }));
    }
  }, []);

  // AI CONTEXT: ux - Real-time form validation
  // Reasoning: Provide immediate feedback for better user experience
  // Constraints: Must validate all required fields including shipping address
  // Future: Consider implementing more sophisticated validation rules

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Phone validation
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Remove all non-digit characters for validation
      const digits = formData.phone.replace(/\D/g, '');
      
      // Check if it's a valid US phone number (10 or 11 digits)
      if (digits.length < 10 || digits.length > 11) {
        newErrors.phone = 'Please enter a valid US phone number (10-11 digits)';
      } else if (digits.length === 11 && !digits.startsWith('1')) {
        newErrors.phone = 'Please enter a valid US phone number starting with 1';
      }
    }
    
    // Birth date validation
    if (!formData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      const today = new Date();
      const birthDate = ensureDate(formData.birthDate);
      if (!birthDate) {
        newErrors.birthDate = 'Please enter a valid birth date';
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age -= 1;
        }

        if (age < 21) {
          newErrors.birthDate = 'You must be 21 or older to register';
        }
      }
    }
    
    // Shipping address validation
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // AI CONTEXT: ux - Handle form input changes
  // Reasoning: Update form state and clear errors as user types
  // Constraints: Must maintain form state consistency
  // Future: Consider implementing auto-save functionality

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // AI CONTEXT: security - Handle form submission with age verification and shipping address
  // Reasoning: Validate age, register user with AWS Cognito, and save shipping address to DynamoDB
  // Constraints: Must verify age and collect shipping address before allowing registration
  // Future: Consider implementing additional verification methods

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Register user with AWS Cognito
      const result = await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        birthDate: formData.birthDate
      });
      
      if (result.success) {
        // Send welcome SMS if API is configured and phone number is provided
        if (formData.phone && isApiConfigured()) {
          try {
            await sendWelcomeSMS(formData.phone);
            console.log('Welcome SMS sent successfully');
          } catch (error) {
            console.warn('Failed to send welcome SMS:', error);
            // Don't fail registration if SMS send fails
          }
        }
        
        // Record referral usage if a referral code was provided (required for admin/dashboard/credit)
        if (formData.referralCode && formData.referralCode.trim() && isApiConfigured()) {
          try {
            const data = await recordReferral(formData.referralCode.trim(), formData.email);
            if (data.success !== false) {
              localStorage.removeItem('pendingReferralCode');
            }
          } catch (error) {
            console.warn('Error recording referral:', error);
            toast.error('Referral could not be recorded. Your account was created—please contact support with your referral code if needed.');
          }
        }
        
        // Save shipping address and SMS consent to DynamoDB if user is automatically signed in
        if (!result.requiresConfirmation) {
          try {
            const profileData = {
              shippingAddress: {
                line1: formData.addressLine1,
                line2: formData.addressLine2,
                city: formData.city,
                state: formData.state,
                postalCode: formData.postalCode,
                country: formData.country
              },
              smsConsent: formData.smsConsent
            };
            await updateMyProfile(profileData);
          } catch (error) {
            console.warn('Failed to save profile data:', error);
            // Don't fail registration if profile save fails
          }
        }
        
        if (result.requiresConfirmation) {
          // Navigate to confirmation page (pass referralCode so ConfirmAccount can retry record-referral if needed)
          navigate('/confirm-account', { 
            state: { 
              email: formData.email,
              password: formData.password,
              phone: formData.phone,
              referralCode: formData.referralCode?.trim() ? formData.referralCode.trim().toUpperCase() : undefined,
              shippingAddress: {
                line1: formData.addressLine1,
                line2: formData.addressLine2,
                city: formData.city,
                state: formData.state,
                postalCode: formData.postalCode,
                country: formData.country
              },
              smsConsent: formData.smsConsent
            }
          });
        } else {
          // User is automatically signed in
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  useEffect(() => {
    setOrCreateMeta('meta[name="robots"]', { name: 'robots' }, 'content', 'noindex,nofollow');
  }, []);

  return (
    <div className="min-h-screen bg-theme-black flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-theme-silver mb-2">{t('auth.createAccount')}</h1>
          <p className="text-theme-silver-light">Join {t('brand.name')} for exclusive giveaways</p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-theme-silver mb-2">
               Full Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.name ? 'border-red-500' : 'border-theme-silver/20'
                }`}
                 placeholder="Enter your full name"
              />
            </div>
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-theme-silver mb-2">
               {t('auth.email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.email ? 'border-red-500' : 'border-theme-silver/20'
                }`}
                 placeholder={t('auth.email')}
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-theme-silver mb-2">
               Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.phone ? 'border-red-500' : 'border-theme-silver/20'
                }`}
                 placeholder="(555) 123-4567"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
            )}
          </div>

          {/* Birth Date Field */}
          <div>
            <label htmlFor="birthDate" className="block text-sm font-medium text-theme-silver mb-2">
               Birth Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type="date"
                id="birthDate"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.birthDate ? 'border-red-500' : 'border-theme-silver/20'
                }`}
              />
            </div>
            {errors.birthDate && (
              <p className="mt-1 text-sm text-red-400">{errors.birthDate}</p>
            )}
            <p className="mt-1 text-xs text-theme-silver-light">
              You must be 21 or older to register
            </p>
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-theme-silver mb-2">
               {t('auth.password')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-12 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.password ? 'border-red-500' : 'border-theme-silver/20'
                }`}
                 placeholder="Create a password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light hover:text-theme-silver"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-400">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-silver mb-2">
               Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-12 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-theme-silver/20'
                }`}
                 placeholder="Confirm your password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light hover:text-theme-silver"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Referral Code Field (Optional) */}
          <div>
            <label htmlFor="referralCode" className="block text-sm font-medium text-theme-silver mb-2">
              Referral Code <span className="text-theme-silver-light text-xs">(Optional)</span>
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
              <input
                type="text"
                id="referralCode"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 border-theme-silver/20 uppercase"
                placeholder="Enter referral code"
                maxLength="8"
              />
            </div>
            <p className="mt-1 text-xs text-theme-silver-light">
              Have a referral code? Enter it here to support your friend!
            </p>
          </div>

          {/* Shipping Address Section */}
          <div className="border-t border-theme-silver/20 pt-6">
            <div className="flex items-center space-x-2 mb-4">
              <MapPin className="h-5 w-5 text-custom-orange" />
              <h3 className="text-lg font-semibold text-theme-silver">Shipping Address</h3>
            </div>
            <p className="text-sm text-theme-silver-light mb-4">
              Required for giveaway prize delivery
            </p>

            {/* Address Line 1 */}
            <div className="mb-4">
              <label htmlFor="addressLine1" className="block text-sm font-medium text-theme-silver mb-2">
                Address Line 1 *
              </label>
              <input
                type="text"
                id="addressLine1"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                  errors.addressLine1 ? 'border-red-500' : 'border-theme-silver/20'
                }`}
                placeholder="123 Main St"
              />
              {errors.addressLine1 && (
                <p className="mt-1 text-sm text-red-400">{errors.addressLine1}</p>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="mb-4">
              <label htmlFor="addressLine2" className="block text-sm font-medium text-theme-silver mb-2">
                Address Line 2
              </label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                className="w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 border-theme-silver/20"
                placeholder="Apt, suite, etc. (optional)"
              />
            </div>

            {/* City and State */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-theme-silver mb-2">
                  City *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.city ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="City"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-400">{errors.city}</p>
                )}
              </div>
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-theme-silver mb-2">
                  State *
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.state ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="State"
                />
                {errors.state && (
                  <p className="mt-1 text-sm text-red-400">{errors.state}</p>
                )}
              </div>
            </div>

            {/* Postal Code and Country */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-theme-silver mb-2">
                  Postal Code *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.postalCode ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="ZIP / Postal code"
                />
                {errors.postalCode && (
                  <p className="mt-1 text-sm text-red-400">{errors.postalCode}</p>
                )}
              </div>
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-theme-silver mb-2">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 border-theme-silver/20"
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          {/* SMS Consent Section */}
          <div className="border-t border-theme-silver/20 pt-6">
            <div className="flex items-center space-x-2 mb-4">
              <MessageSquare className="h-5 w-5 text-custom-orange" />
              <h3 className="text-lg font-semibold text-theme-silver">SMS Notifications</h3>
            </div>
            <p className="text-sm text-theme-silver-light mb-4">
              Stay updated on giveaways, exclusive offers, and important updates
            </p>
            
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="smsConsent"
                name="smsConsent"
                checked={formData.smsConsent}
                onChange={handleInputChange}
                className="mt-1 h-4 w-4 text-custom-orange focus:ring-custom-orange border-theme-silver/20 rounded"
              />
              <div className="flex-1">
                <label htmlFor="smsConsent" className="text-sm text-theme-silver">
                  I consent to receive SMS messages from {t('brand.name')}, including order confirmations, shipping updates, product alerts, cart reminders, and giveaway notifications. Msg & data rates may apply. Reply STOP to unsubscribe. Up to 4 msgs/month.
                </label>
                <p className="text-xs text-theme-silver-light mt-1">
                  <Link to="/legal#privacy" className="text-custom-orange hover:text-custom-orange/80">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/legal#terms" className="text-custom-orange hover:text-custom-orange/80">
                    Terms of Service
                  </Link>{' '}
                  apply. Consent is not required as a condition of purchase.
                </p>
            </div>
          </div>
        </div>

        {/* Push Notification Consent Section - DISABLED */}

        {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
             {loading ? 'Creating Account...' : t('auth.createAccountCta')}
          </button>
        </form>

        {/* Login Link */}
        <div className="text-center mt-6">
          <p className="text-theme-silver-light">
            Already have an account?{' '}
            <Link to="/login" className="text-custom-orange hover:text-custom-orange/80 font-medium">
              {t('auth.signin')}
            </Link>
          </p>
        </div>

        {/* Legal Notice */}
        <div className="mt-8 glass-card-dark p-4 rounded-lg">
          <p className="text-xs text-theme-silver-light text-center">
            By creating an account, you agree to our{' '}
            <Link to="/legal" className="text-custom-orange hover:text-custom-orange/80">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/legal" className="text-custom-orange hover:text-custom-orange/80">
              Privacy Policy
            </Link>
            . You must be 21 or older to participate in firearm giveaways.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register; 